import { useEffect } from 'react';

/* ─────────────────────────────────────────────
   KFX JOURNAL — PRIVACY POLICY
   Effective date: June 2026
───────────────────────────────────────────── */

const LAST_UPDATED = 'June 6, 2026';
const CONTACT_EMAIL = 'info@kfxjournal.com';
const COMPANY_NAME = 'KFX Journal';

const sections = [
  {
    id: 'overview',
    title: '1. Overview',
    content: [
      {
        type: 'text',
        text: `${COMPANY_NAME} ("KFX", "we", "our", or "us") is a trading intelligence platform that helps forex traders analyse their performance, identify patterns, and improve through AI-powered coaching. This Privacy Policy explains what data we collect, why we collect it, how we use it, and your rights over it.`,
      },
      {
        type: 'text',
        text: 'By creating an account or using KFX Journal, you agree to the practices described in this policy. If you do not agree, please do not use the platform.',
      },
      {
        type: 'highlight',
        text: 'Your trade data is deeply personal financial information. We treat it with the highest level of care. We do not sell your data, share it with advertisers, or use it to train AI models.',
      },
    ],
  },
  {
    id: 'data-we-collect',
    title: '2. Data We Collect',
    content: [
      {
        type: 'text',
        text: 'We collect the minimum data necessary to provide the service. This falls into three categories:',
      },
      {
        type: 'subheading',
        text: '2.1 Account Data',
      },
      {
        type: 'list',
        items: [
          'Email address — used for login and account communications',
          'Password — stored as a bcrypt hash; we never store your plain-text password',
          'Display name — optional, used in your dashboard',
          'Account creation date and last login timestamp',
          'Your plan type (Free, Pro, or Lifetime)',
        ],
      },
      {
        type: 'subheading',
        text: '2.2 Trade & Journal Data',
      },
      {
        type: 'list',
        items: [
          'Trade records you log manually (pair, direction, entry/exit price, size, P&L, date/time)',
          'Trade records imported via MT4/MT5 CSV export',
          'Execution grades you assign (A/B/C)',
          'Session tags, strategy tags, and emotion labels',
          'Journal notes and trade debriefs you write',
          'Screenshots you upload alongside trade records',
          'Trading rules you define in your rule set',
        ],
      },
      {
        type: 'subheading',
        text: '2.3 Usage & Technical Data',
      },
      {
        type: 'list',
        items: [
          'Browser type and version',
          'Operating system',
          'IP address (used for security, not tracking)',
          'Pages visited and features used within the app',
          'Error logs and crash reports',
          'Session duration and login frequency',
        ],
      },
    ],
  },
  {
    id: 'how-we-use',
    title: '3. How We Use Your Data',
    content: [
      {
        type: 'text',
        text: 'We use your data exclusively to provide and improve KFX Journal. Specifically:',
      },
      {
        type: 'list',
        items: [
          'To display your trade history, analytics, and equity curve in your dashboard',
          'To calculate your win rate, average R:R, session performance, and rule adherence score',
          'To generate your AI coaching report — the AI reads your trade data within the request and does not retain it',
          'To send account emails (password reset, account confirmation, product updates)',
          'To identify and fix bugs and performance issues',
          'To improve the platform based on aggregated, anonymised usage patterns',
        ],
      },
      {
        type: 'highlight',
        text: 'We do not use your individual trade data for advertising, profiling, or sharing with third parties. Your data is used to serve you — nothing else.',
      },
    ],
  },
  {
    id: 'ai-and-your-data',
    title: '4. AI Coaching & Your Trade Data',
    content: [
      {
        type: 'text',
        text: 'KFX Journal uses Anthropic\'s Claude API to generate personalised coaching reports. This section explains exactly how that works.',
      },
      {
        type: 'subheading',
        text: 'How it works',
      },
      {
        type: 'text',
        text: 'When you request an AI coaching report, your trade history for the selected period is sent to Anthropic\'s API as part of the prompt. Claude analyses the data and returns a coaching report. The request is stateless — no conversation history is stored between sessions.',
      },
      {
        type: 'subheading',
        text: 'What Anthropic receives',
      },
      {
        type: 'list',
        items: [
          'Your trade records for the selected period (pair, direction, P&L, grade, session)',
          'Aggregated performance statistics (win rate, average R:R, rule adherence score)',
          'Your defined trading rules (if you have added them)',
          'No personally identifiable information is included in the AI prompt — no name, email, or account ID',
        ],
      },
      {
        type: 'subheading',
        text: 'Model training',
      },
      {
        type: 'highlight',
        text: 'Your trade data is NOT used to train any AI model. Anthropic\'s API operates under zero-data-retention terms for business API users. Your financial data does not leave the request lifecycle.',
      },
      {
        type: 'text',
        text: 'For more information on how Anthropic handles API data, visit anthropic.com/privacy.',
      },
    ],
  },
  {
    id: 'data-storage',
    title: '5. Data Storage & Security',
    content: [
      {
        type: 'subheading',
        text: 'Encryption',
      },
      {
        type: 'list',
        items: [
          'All data is encrypted in transit using TLS 1.3',
          'All data is encrypted at rest using AES-256',
          'Passwords are hashed using bcrypt with a minimum cost factor of 12',
          'JWT authentication tokens are stored in HttpOnly cookies — not accessible to JavaScript',
        ],
      },
      {
        type: 'subheading',
        text: 'Database',
      },
      {
        type: 'text',
        text: 'Your data is stored in a PostgreSQL database managed via Prisma ORM. Database access is restricted to application servers only. No direct public access is permitted.',
      },
      {
        type: 'subheading',
        text: 'Backups',
      },
      {
        type: 'text',
        text: 'Database backups are taken daily and retained for 30 days. Backups are encrypted using the same AES-256 standard.',
      },
      {
        type: 'subheading',
        text: 'Access controls',
      },
      {
        type: 'text',
        text: 'Only the founding team has access to production systems. We do not use offshore contractors with access to user data. All access is logged and audited.',
      },
    ],
  },
  {
    id: 'data-sharing',
    title: '6. Data Sharing & Third Parties',
    content: [
      {
        type: 'highlight',
        text: 'We do not sell your data. We do not share your data with advertisers. We do not share your individual data with any third party except as described below.',
      },
      {
        type: 'text',
        text: 'We work with a small number of third-party service providers necessary to operate the platform:',
      },
      {
        type: 'table',
        rows: [
          { provider: 'Anthropic', purpose: 'AI coaching report generation via Claude API', data: 'Anonymised trade data per request' },
          { provider: 'Hosting provider', purpose: 'Server and database infrastructure', data: 'All platform data (encrypted)' },
          { provider: 'Email service', purpose: 'Transactional emails (account, password reset)', data: 'Email address only' },
        ],
      },
      {
        type: 'text',
        text: 'All third-party providers are contractually required to handle your data only for the specified purpose and in accordance with applicable data protection law.',
      },
    ],
  },
  {
    id: 'your-rights',
    title: '7. Your Rights & Controls',
    content: [
      {
        type: 'text',
        text: 'You have full control over your data. At any time you can:',
      },
      {
        type: 'list',
        items: [
          'Access all your data — your full trade history, journal entries, and account information are visible in your dashboard at all times',
          'Export your data — download your complete trade history as a CSV at any time from Settings → Export',
          'Delete individual trades — remove any trade record permanently from your journal',
          'Delete your account — permanently delete your account and all associated data from Settings → Account → Delete Account. Deletion is processed within 24 hours and is irreversible.',
          'Correct your data — update your account details, trade records, or journal entries at any time',
          'Opt out of product emails — unsubscribe from non-essential communications via the link in any email',
        ],
      },
      {
        type: 'text',
        text: `To exercise any of these rights or if you have questions, contact us at ${CONTACT_EMAIL}.`,
      },
    ],
  },
  {
    id: 'data-retention',
    title: '8. Data Retention',
    content: [
      {
        type: 'text',
        text: 'We retain your data for as long as your account is active. Specifically:',
      },
      {
        type: 'list',
        items: [
          'Active account data — retained indefinitely while your account is open',
          'Deleted trades — removed immediately and permanently on deletion',
          'Deleted accounts — all user data removed within 24 hours of account deletion',
          'Anonymised usage analytics — retained for up to 24 months to improve the platform',
          'Error logs — retained for 90 days for debugging purposes, then deleted',
          'Backup data — retained for 30 days, then permanently overwritten',
        ],
      },
    ],
  },
  {
    id: 'cookies',
    title: '9. Cookies',
    content: [
      {
        type: 'text',
        text: 'We use a minimal number of cookies, all necessary for the platform to function:',
      },
      {
        type: 'list',
        items: [
          'Authentication cookie — an HttpOnly JWT token that keeps you logged in. Expires after 7 days of inactivity.',
          'Theme preference cookie — stores your dark/light mode preference. No personal data.',
          'Session cookie — a temporary session identifier deleted when you close your browser.',
        ],
      },
      {
        type: 'text',
        text: 'We do not use advertising cookies, tracking pixels, or third-party analytics cookies (such as Google Analytics). We do not track you across other websites.',
      },
    ],
  },
  {
    id: 'children',
    title: '10. Children\'s Privacy',
    content: [
      {
        type: 'text',
        text: 'KFX Journal is not intended for use by anyone under the age of 18. We do not knowingly collect personal data from minors. If you believe a minor has created an account, please contact us immediately at info@kfxjournal.com and we will delete the account.',
      },
    ],
  },
  {
    id: 'changes',
    title: '11. Changes to This Policy',
    content: [
      {
        type: 'text',
        text: 'We may update this Privacy Policy from time to time. When we make material changes, we will notify you by email and display a notice in the app at least 14 days before the changes take effect. The "Last Updated" date at the top of this page reflects the most recent revision.',
      },
      {
        type: 'text',
        text: 'Continued use of KFX Journal after the effective date of any changes constitutes your acceptance of the updated policy.',
      },
    ],
  },
  {
    id: 'contact',
    title: '12. Contact',
    content: [
      {
        type: 'text',
        text: 'If you have questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us:',
      },
      {
        type: 'contact',
        items: [
          { label: 'Email', value: CONTACT_EMAIL },
          { label: 'Product', value: 'KFX Journal — Trading Intelligence Platform' },
          { label: 'Response time', value: 'Within 2 business days' },
        ],
      },
    ],
  },
];

function PolicySection({ section }) {
  return (
    <div id={section.id} style={{ marginBottom: 48, scrollMarginTop: 80 }}>
      <h2 style={{
        fontSize: 18, fontWeight: 700, color: 'var(--text)',
        letterSpacing: '-0.01em', marginBottom: 18,
        paddingBottom: 12, borderBottom: '1px solid rgba(212,168,67,0.12)',
        fontFamily: "'Bricolage Grotesque',sans-serif",
      }}>
        {section.title}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {section.content.map((block, i) => {
          if (block.type === 'text') return (
            <p key={i} style={{ fontSize: 14.5, color: 'var(--text-60)', lineHeight: 1.85, fontFamily: "'Bricolage Grotesque',sans-serif" }}>
              {block.text}
            </p>
          );
          if (block.type === 'subheading') return (
            <h3 key={i} style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', letterSpacing: '0.01em', marginTop: 8, fontFamily: "'Bricolage Grotesque',sans-serif" }}>
              {block.text}
            </h3>
          );
          if (block.type === 'highlight') return (
            <div key={i} style={{
              padding: '14px 16px', borderRadius: 10,
              background: 'rgba(212,168,67,0.06)',
              border: '1px solid rgba(212,168,67,0.2)',
              borderLeft: '3px solid rgba(212,168,67,0.6)',
            }}>
              <p style={{ fontSize: 14, color: 'var(--text-70)', lineHeight: 1.8, fontFamily: "'Bricolage Grotesque',sans-serif", margin: 0 }}>
                {block.text}
              </p>
            </div>
          );
          if (block.type === 'list') return (
            <ul key={i} style={{ paddingLeft: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8, listStyle: 'none' }}>
              {block.items.map((item, j) => (
                <li key={j} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(212,168,67,0.6)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 4 }}>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span style={{ fontSize: 14, color: 'var(--text-55)', lineHeight: 1.75, fontFamily: "'Bricolage Grotesque',sans-serif" }}>{item}</span>
                </li>
              ))}
            </ul>
          );
          if (block.type === 'table') return (
            <div key={i} style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 400 }}>
                <thead>
                  <tr>
                    {['Service Provider', 'Purpose', 'Data Shared'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 10, fontFamily: "'DM Mono',monospace", letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(212,168,67,0.7)', borderBottom: '1px solid rgba(212,168,67,0.15)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, j) => (
                    <tr key={j} style={{ borderBottom: '1px solid var(--overlay-5)' }}>
                      <td style={{ padding: '10px 12px', color: 'var(--text-70)', fontWeight: 600, fontFamily: "'Bricolage Grotesque',sans-serif" }}>{row.provider}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-55)', fontFamily: "'Bricolage Grotesque',sans-serif" }}>{row.purpose}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-45)', fontFamily: "'Bricolage Grotesque',sans-serif" }}>{row.data}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
          if (block.type === 'contact') return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '16px 20px', borderRadius: 10, background: 'var(--overlay-2)', border: '1px solid var(--overlay-7)' }}>
              {block.items.map((item, j) => (
                <div key={j} className="kfx-contact-row" style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: 'rgba(212,168,67,0.6)', letterSpacing: '0.1em', textTransform: 'uppercase', minWidth: 100, flexShrink: 0 }}>{item.label}</span>
                  <span style={{ fontSize: 14, color: 'var(--text-65)', fontFamily: "'Bricolage Grotesque',sans-serif', wordBreak: 'break-all'" }}>
                    {item.label === 'Email' ? <a href={`mailto:${item.value}`} style={{ color: 'var(--gold)', textDecoration: 'none', wordBreak: 'break-all' }}>{item.value}</a> : item.value}
                  </span>
                </div>
              ))}
            </div>
          );
          return null;
        })}
      </div>
    </div>
  );
}

export default function PrivacyPolicy({ onNavigate }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg, #070707)',
      color: 'var(--text, #EDE8DE)',
      fontFamily: "'Bricolage Grotesque',sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,700;12..96,800&family=DM+Mono:wght@400;500&display=swap');
        :root {
          --bg: #070707; --surface: #0f0f0d; --text: #EDE8DE;
          --text-70: rgba(237,232,222,0.70); --text-65: rgba(237,232,222,0.65);
          --text-60: rgba(237,232,222,0.60); --text-55: rgba(237,232,222,0.55);
          --text-45: rgba(237,232,222,0.45);
          --gold: #D4A843; --overlay-2: rgba(255,255,255,0.02);
          --overlay-5: rgba(255,255,255,0.05); --overlay-7: rgba(255,255,255,0.07);
        }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }

        /* ── Mobile ── */
        @media (max-width: 700px) {
          /* Layout: single column */
          .kfx-layout {
            grid-template-columns: 1fr !important;
            gap: 0 !important;
            padding: 32px 16px 72px !important;
          }

          /* Sidebar: collapse to compact card above content */
          .kfx-sidebar {
            position: static !important;
            margin-bottom: 28px;
            padding: 12px 14px;
            background: rgba(255,255,255,0.02);
            border: 1px solid rgba(212,168,67,0.1);
            border-radius: 10px;
          }
          .kfx-sidebar-label { margin-bottom: 10px !important; }
          .kfx-toc-nav {
            flex-direction: row !important;
            flex-wrap: wrap !important;
            gap: 4px !important;
          }
          .kfx-toc-nav a {
            font-size: 11px !important;
            padding: 4px 8px !important;
            background: rgba(212,168,67,0.04) !important;
            border-radius: 5px;
            line-height: 1.3 !important;
          }

          /* Nav: tighten padding, hide subtitle */
          .kfx-nav { padding: 0 16px !important; }
          .kfx-nav-subtitle { display: none !important; }
          .kfx-back-btn {
            font-size: 10px !important;
            padding: 6px 12px !important;
            letter-spacing: 0.04em !important;
          }

          /* Header section */
          .kfx-page-header { margin-bottom: 32px !important; }
          .kfx-page-header h1 { font-size: 28px !important; }

          /* Section spacing */
          .kfx-section { margin-bottom: 36px !important; }


        }
      `}</style>

      {/* Nav */}
      <nav className="kfx-nav" style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(7,7,7,0.92)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(212,168,67,0.1)', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={() => onNavigate('home')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' }}>KFX Journal</div>
          <div className="kfx-nav-subtitle" style={{ fontSize: 8, color: 'rgba(212,168,67,0.5)', fontFamily: "'DM Mono',monospace", letterSpacing: '0.1em', textTransform: 'uppercase' }}>Trading Intelligence</div>
        </button>
        <button className="kfx-back-btn" onClick={() => onNavigate('home')} style={{ background: 'none', border: '1px solid rgba(212,168,67,0.2)', borderRadius: 7, padding: '7px 16px', color: 'var(--gold)', fontSize: 11, fontFamily: "'DM Mono',monospace", letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,168,67,0.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
        >← Back</button>
      </nav>

      <div className="kfx-layout" style={{ maxWidth: 860, margin: '0 auto', padding: '72px 24px 120px', display: 'grid', gridTemplateColumns: '220px 1fr', gap: 60, alignItems: 'start' }}>

        {/* Sidebar TOC */}
        <aside className="kfx-sidebar" style={{ position: 'sticky', top: 80 }}>
          <div className="kfx-sidebar-label" style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: 'rgba(212,168,67,0.6)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 16 }}>On this page</div>
          <nav className="kfx-toc-nav" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {sections.map(s => (
              <a key={s.id} href={`#${s.id}`} style={{ fontSize: 13, color: 'var(--text-45)', textDecoration: 'none', padding: '5px 10px', borderRadius: 6, transition: 'all 0.2s', fontFamily: "'Bricolage Grotesque',sans-serif", lineHeight: 1.4 }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.background = 'rgba(212,168,67,0.06)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-45)'; e.currentTarget.style.background = 'none'; }}
              >{s.title}</a>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main>
          {/* Header */}
          <div className="kfx-page-header" style={{ marginBottom: 56 }}>
            <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: 'rgba(212,168,67,0.6)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>Legal</div>
            <h1 style={{ fontSize: 'clamp(28px,5vw,48px)', fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.025em', lineHeight: 1.1, marginBottom: 16 }}>Privacy Policy</h1>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--text-45)', fontFamily: "'DM Mono',monospace" }}>Last updated: {LAST_UPDATED}</span>
              <span style={{ fontSize: 12, color: 'var(--text-45)', fontFamily: "'DM Mono',monospace" }}>Effective: {LAST_UPDATED}</span>
            </div>
            <div style={{ marginTop: 20, padding: '14px 16px', borderRadius: 8, background: 'rgba(46,204,138,0.06)', border: '1px solid rgba(46,204,138,0.2)' }}>
              <p style={{ fontSize: 13.5, color: 'rgba(46,204,138,0.85)', lineHeight: 1.7, fontFamily: "'Bricolage Grotesque',sans-serif" }}>
                <strong>Short version:</strong> We collect only what we need to run the platform. We never sell your data, never share it with advertisers, and the AI coach never trains on your trades. You can export or delete everything at any time.
              </p>
            </div>
          </div>

          {/* Sections */}
          {sections.map(section => (
            <PolicySection key={section.id} section={section} />
          ))}
        </main>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid rgba(212,168,67,0.1)', padding: '28px 24px', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: '6px 10px', fontSize: 12, color: 'var(--text-45)', fontFamily: "'DM Mono',monospace", letterSpacing: '0.04em' }}>
          <span>© {new Date().getFullYear()} KFX Journal</span>
          <span style={{ opacity: 0.35 }}>·</span>
          <button onClick={() => onNavigate('terms')} style={{ color: 'var(--text-45)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Mono',monospace", fontSize: 12, padding: 0, letterSpacing: '0.04em' }}>Terms of Service</button>
          <span style={{ opacity: 0.35 }}>·</span>
          <button onClick={() => onNavigate('privacy')} style={{ color: 'var(--gold)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Mono',monospace", fontSize: 12, padding: 0, letterSpacing: '0.04em' }}>Privacy Policy</button>
          <span style={{ opacity: 0.35 }}>·</span>
          <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--text-45)', textDecoration: 'none' }}>{CONTACT_EMAIL}</a>
        </div>
      </div>
    </div>
  );
}